import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import * as A from './mediaManagementActions';
import type { MediaToast, SaveMediaPayload } from './mediaManagementTypes';

/** Media Management server state (P1, categories, save, sync) plus bound dispatchers for the screens. */
export function useMediaManagementStore() {
  const dispatch = useAppDispatch();
  const locationId = useAppSelector((s) => s.mediaManagement.locationId);
  const data = useAppSelector((s) => s.mediaManagement.data);
  const loading = useAppSelector((s) => s.mediaManagement.loading);
  const loadError = useAppSelector((s) => s.mediaManagement.loadError);
  const saving = useAppSelector((s) => s.mediaManagement.saving);
  const progress = useAppSelector((s) => s.mediaManagement.progress);
  const lastSave = useAppSelector((s) => s.mediaManagement.lastSave);
  const syncBusy = useAppSelector((s) => s.mediaManagement.syncBusy);
  const canEdit = useAppSelector((s) => s.mediaManagement.access.canEdit);

  return useMemo(
    () => ({
      locationId,
      data,
      loading,
      loadError,
      saving,
      progress,
      lastSave,
      syncBusy,
      canEdit,
      openLocation: (id: string) => dispatch(A.openLocation(id)),
      reload: (id: string) => dispatch(A.loadMediaRequest(id)),
      save: (payload: SaveMediaPayload) => dispatch(A.saveMediaRequest(payload)),
      sync: (id: string) => dispatch(A.syncMediaRequest(id)),
      addToast: (message: string, type?: MediaToast['type'], placement?: MediaToast['placement']) =>
        dispatch(A.addMediaToast(message, type, placement)),
    }),
    [dispatch, locationId, data, loading, loadError, saving, progress, lastSave, syncBusy, canEdit],
  );
}

/** Product Catalog categories of the outlet, read by Media Management's own saga. */
export function useMediaCategories() {
  const dispatch = useAppDispatch();
  const categories = useAppSelector((s) => s.mediaManagement.categories);
  const loadedLocationId = useAppSelector((s) => s.mediaManagement.categoriesLocationId);
  const error = useAppSelector((s) => s.mediaManagement.categoriesError);

  return useMemo(
    () => ({
      categories,
      loadedLocationId,
      error,
      load: (locationId: string) => dispatch(A.loadCategoriesRequest(locationId)),
    }),
    [dispatch, categories, loadedLocationId, error],
  );
}

/** Kiosk Media permissions of the signed-in employee. `load` reads them again. */
export function useKioskMediaAccess() {
  const dispatch = useAppDispatch();
  const access = useAppSelector((s) => s.mediaManagement.access);
  const load = useCallback(() => dispatch(A.loadMediaAccessRequest()), [dispatch]);
  return useMemo(() => ({ ...access, load }), [access, load]);
}

export function useMediaToastsStore() {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector((s) => s.mediaManagement.toasts);
  return useMemo(
    () => ({ toasts, dismiss: (id: number) => dispatch(A.dismissMediaToast(id)) }),
    [dispatch, toasts],
  );
}
